import { ref, computed, reactive } from "vue";

/**
 * @composable useListFilters
 * @description Gestisce i filtri per una lista con la possibilità di resettare e tracciare i filtri attivi.
 * @param {Object} defaults - Oggetto con i valori di default dei filtri
 * @param {Object} options - Opzioni di configurazione
 * @param {Array<string>} [options.ignore=[]] - Array di chiavi da ignorare quando si verifica se ci sono filtri attivi
 * @returns {{
 *   filters: Object,
 *   hasActiveFilters: import('vue').ComputedRef<boolean>,
 *   resetFilters: Function,
 *   resetKey: import('vue').Ref<number>
 * }}
 */
export function useListFilters(defaults, { ignore = [] } = {}) {
  // Crea una copia reattiva dei filtri di default
  const filters = reactive({ ...defaults });

  // Contatore per rimontare input non controllati (es. TextSearchInput)
  const resetKey = ref(0);

  /**
   * Computed che verifica se ci sono filtri attivi
   * (differenze dai default escludendo le chiavi in ignore)
   */
  const hasActiveFilters = computed(() => {
    for (const key in defaults) {
      // Ignora le chiavi specificate
      if (ignore.includes(key)) {
        continue;
      }
      // Se il valore è diverso dal default, allora ci sono filtri attivi
      if (filters[key] !== defaults[key]) {
        return true;
      }
    }
    return false;
  });

  /**
   * Resetta i filtri ai valori di default e incrementa resetKey
   */
  function resetFilters() {
    for (const key in defaults) {
      filters[key] = defaults[key];
    }
    resetKey.value++;
  }

  return {
    filters,
    hasActiveFilters,
    resetFilters,
    resetKey,
  };
}
